import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Select from "react-select";
import { createUserWithCentre, getUserCentresByRole, updateUserWithCentreRole } from '@app/services/user';
import { getCentresByFranchiseId } from '@app/services/centre';
import { getAllActiveFranchise } from '@app/services/franchise';
import { toast } from 'react-toastify';
import { Button } from '@app/components';
import { Modal } from 'react-bootstrap';

const AddUpdateCentreUserModal = (props: any) => {

    const isEdit = !!props.editUser;
    const [franchises, setFranchises] = useState<any[]>([]);
    const [selectedFranchiseIds, setSelectedFranchiseIds] = useState<number[]>([]);
    const [centres, setCentres] = useState<any[]>([]);
    const [isLoading, setLoading] = useState(false);
    const [isLoadingRoleCentres, setLoadingRoleCentres] = useState(false);

    useEffect(() => {
        if (props.show) fetchFranchises();
    }, [props.show]);

    useEffect(() => {
        // Pre-select franchise from props (when opened from ViewCentre)
        if (props.franchiseId && !isEdit) {
            setSelectedFranchiseIds([props.franchiseId]);
            fetchCentresForFranchises([props.franchiseId]);
        }
    }, [props.franchiseId]);

    useEffect(() => {
        // On edit, pre-fill the franchise/centre pickers with whatever centres
        // this user currently holds under their initial centre role. A centre-
        // scoped staff list only reflects the centre being viewed, so we read
        // the user's full per-role centre list instead of trusting props.editUser.
        if (props.show && isEdit && props.editUser?.id) {
            const initialRoleId = props.editUser?.roles?.find((r: any) => props.centreRoles?.some((cr: any) => cr.id === r.id))?.id;
            if (initialRoleId) {
                loadCentresForRole(props.editUser.id, initialRoleId);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.show, props.editUser?.id]);

    const fetchFranchises = async () => {
        try {
            const resp = await getAllActiveFranchise();
            if (resp?.success === true) setFranchises(resp.collections || []);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load franchises');
        }
    };

    const fetchCentresForFranchises = async (franchiseIds: number[]) => {
        if (!franchiseIds.length) { setCentres([]); return; }
        try {
            const results = await Promise.all(franchiseIds.map(id => getCentresByFranchiseId(id)));
            const allCentres = results.flatMap(r => r?.success ? r.collections || [] : []);
            // Deduplicate by id
            const unique = allCentres.filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i);
            setCentres(unique);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load centres');
        }
    };

    // Loads the centres (and their franchises) the user holds under one
    // specific role, so switching the Role dropdown while editing shows that
    // role's own centres rather than whatever was previously selected.
    const loadCentresForRole = async (userId: number, roleId: number) => {
        try {
            setLoadingRoleCentres(true);
            const resp = await getUserCentresByRole(userId, roleId);
            if (resp?.success === true) {
                const franchiseIds = resp?.collections?.franchise_ids || [];
                const roleCentres = resp?.collections?.centres || [];
                setSelectedFranchiseIds(franchiseIds);
                setFieldValue('centre_ids', roleCentres.map((c: any) => c.centre_id));
                if (franchiseIds.length > 0) {
                    await fetchCentresForFranchises(franchiseIds);
                } else {
                    setCentres([]);
                }
            } else {
                toast.error(resp?.errors?.[0]?.msg || resp?.message || 'Failed to load centres for role');
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal server error');
        } finally {
            setLoadingRoleCentres(false);
        }
    };

    const { handleChange, values, handleSubmit, touched, errors, setFieldValue, resetForm } = useFormik({
        enableReinitialize: true,
        initialValues: {
            first_name: props.editUser?.first_name || '',
            last_name: props.editUser?.last_name || '',
            email: props.editUser?.email || '',
            alt_email: props.editUser?.alt_email || '',
            phone: props.editUser?.phone || '',
            alt_phone: props.editUser?.alt_phone || '',
            address: props.editUser?.address || '',
            role_id: props.editUser?.roles?.find((r: any) => props.centreRoles?.some((cr: any) => cr.id === r.id))?.id?.toString() || '',
            status: props.editUser?.status || 'active',
            centre_ids: [] as number[],
        },
        validationSchema: Yup.object({
            first_name: Yup.string().required('Please enter first name'),
            last_name: Yup.string().required('Please enter last name'),
            email: Yup.string().required('Please enter email').email('Enter valid email'),
            phone: Yup.string().required('Please enter mobile number')
                .matches(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/, 'Please enter valid number'),
            alt_phone: Yup.string().matches(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/, 'Please enter valid number'),
            address: Yup.string().required('Please enter address'),
            role_id: Yup.string().required('Please select role'),
            centre_ids: Yup.array().min(1, 'Please select at least one centre'),
        }),
        onSubmit: async (vals) => {
            try {
                setLoading(true);
                let resp;
                if (isEdit) {
                    const u = props.editUser;
                    // /admin/users/with-centre/update replaces centres for exactly one
                    // role: it adds role_id if the user doesn't already hold it, and
                    // only touches that role's centre grants -- any other role (another
                    // centre role, or an FMS-A role) is left completely untouched. This
                    // avoids reconstructing the user's full role/centre set on the
                    // frontend, which previously caused other roles to be dropped.
                    resp = await updateUserWithCentreRole({
                        user_id: u.id,
                        first_name: vals.first_name,
                        last_name: vals.last_name,
                        email: vals.email,
                        alt_email: vals.alt_email || '',
                        phone: vals.phone || '',
                        alt_phone: vals.alt_phone || '',
                        address: vals.address || '',
                        status: vals.status,
                        role_id: parseInt(vals.role_id),
                        centre_ids: vals.centre_ids,
                    });
                } else {
                    resp = await createUserWithCentre({
                        first_name: vals.first_name,
                        last_name: vals.last_name,
                        email: vals.email,
                        phone: vals.phone,
                        alt_phone: vals.alt_phone || undefined,
                        address: vals.address,
                        status: vals.status,
                        role_id: parseInt(vals.role_id),
                        centre_ids: vals.centre_ids,
                    });
                }
                if (resp?.success === true) {
                    toast.success(resp?.message || (isEdit ? 'User updated successfully' : 'User created successfully'));
                    resetForm();
                    props.handleClose();
                    if (props.onSuccess) props.onSuccess(resp);
                    if (props.setUserFlag) props.setUserFlag(true);
                    setSelectedFranchiseIds([]);
                    setCentres([]);
                } else {
                    toast.error(resp?.errors?.[0]?.msg || resp?.message || 'Failed');
                }
            } catch (error: any) {
                toast.error(error?.message || 'Internal server error');
            } finally {
                setLoading(false);
            }
        }
    });

    return (
        <Modal show={props.show} onHide={props.handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{isEdit ? 'Edit Centre User' : 'Add New Centre User'}</Modal.Title>
            </Modal.Header>
            <form onSubmit={handleSubmit}>
                <Modal.Body>
                    <div className="row">
                        <div className="col-sm-6">
                            <div className="form-group">
                                <label>First Name <span className="required">*</span></label>
                                <input type="text" className="form-control" placeholder="Enter first name"
                                    name="first_name" onChange={handleChange} value={values.first_name} />
                                {touched.first_name && errors.first_name && <div className="text-danger">{errors.first_name}</div>}
                            </div>
                        </div>
                        <div className="col-sm-6">
                            <div className="form-group">
                                <label>Last Name <span className="required">*</span></label>
                                <input type="text" className="form-control" placeholder="Enter last name"
                                    name="last_name" onChange={handleChange} value={values.last_name} />
                                {touched.last_name && errors.last_name && <div className="text-danger">{errors.last_name}</div>}
                            </div>
                        </div>
                        <div className="col-sm-6">
                            <div className="form-group">
                                <label>Email <span className="required">*</span></label>
                                <input type="email" className="form-control" placeholder="Enter email"
                                    name="email" onChange={handleChange} value={values.email} />
                                {touched.email && errors.email && <div className="text-danger">{errors.email}</div>}
                            </div>
                        </div>
                        <div className="col-sm-6">
                            <div className="form-group">
                                <label>Alternate Email</label>
                                <input type="email" className="form-control" placeholder="Enter alternate email"
                                    name="alt_email" onChange={handleChange} value={values.alt_email} />
                            </div>
                        </div>
                        <div className="col-sm-6">
                            <div className="form-group">
                                <label>Mobile <span className="required">*</span></label>
                                <input type="text" className="form-control" placeholder="Enter mobile number"
                                    name="phone" onChange={handleChange} value={values.phone} />
                                {touched.phone && errors.phone && <div className="text-danger">{errors.phone}</div>}
                            </div>
                        </div>
                        <div className="col-sm-6">
                            <div className="form-group">
                                <label>Alternate Number</label>
                                <input type="text" className="form-control" placeholder="Enter alternate number"
                                    name="alt_phone" onChange={handleChange} value={values.alt_phone} />
                                {touched.alt_phone && errors.alt_phone && <div className="text-danger">{errors.alt_phone}</div>}
                            </div>
                        </div>
                        <div className="col-sm-6">
                            <div className="form-group">
                                <label>Address <span className="required">*</span></label>
                                <textarea className="form-control" placeholder="Enter address"
                                    name="address" onChange={handleChange} value={values.address} />
                                {touched.address && errors.address && <div className="text-danger">{errors.address}</div>}
                            </div>
                        </div>
                        <div className="col-sm-6">
                            <div className="form-group">
                                <label>Role <span className="required">*</span></label>
                                <select
                                    className="form-control"
                                    name="role_id"
                                    onChange={(e) => {
                                        handleChange(e);
                                        const newRoleId = parseInt(e.target.value);
                                        if (isEdit && props.editUser?.id && newRoleId) {
                                            // Reload this user's centres for the newly selected
                                            // role, since each role can have a different set.
                                            loadCentresForRole(props.editUser.id, newRoleId);
                                        }
                                    }}
                                    value={values.role_id}
                                >
                                    <option value="">-- Please select --</option>
                                    {(props.centreRoles || []).map((r: any) => (
                                        <option key={r.id} value={r.id}>{r.name}</option>
                                    ))}
                                </select>
                                {touched.role_id && errors.role_id && <div className="text-danger">{errors.role_id}</div>}
                            </div>
                        </div>
                        <div className="col-sm-6">
                            <div className="form-group">
                                <label>Franchise(s) <span className="required">*</span></label>
                                <Select
                                    isMulti
                                    isDisabled={isLoadingRoleCentres}
                                    placeholder="Select franchise(s)"
                                    options={franchises.map((f: any) => ({ value: f.id, label: f.name }))}
                                    onChange={(opts: any) => {
                                        const ids = opts ? opts.map((o: any) => o.value) : [];
                                        setSelectedFranchiseIds(ids);
                                        fetchCentresForFranchises(ids);
                                        setFieldValue('centre_ids', []);
                                    }}
                                    value={franchises.filter((f: any) => selectedFranchiseIds.includes(f.id)).map((f: any) => ({ value: f.id, label: f.name }))}
                                    closeMenuOnSelect={false}
                                />
                            </div>
                        </div>
                        <div className="col-sm-6">
                            <div className="form-group">
                                <label>Assign Centre(s) <span className="required">*</span></label>
                                <Select
                                    isMulti
                                    isDisabled={isLoadingRoleCentres}
                                    placeholder="Select centre(s)"
                                    options={centres.map((c: any) => ({ value: c.id, label: c.name }))}
                                    onChange={(opts: any) => setFieldValue('centre_ids', opts ? opts.map((o: any) => o.value) : [])}
                                    value={centres.filter((c: any) => values.centre_ids.includes(c.id)).map((c: any) => ({ value: c.id, label: c.name }))}
                                    closeMenuOnSelect={false}
                                />
                                {touched.centre_ids && errors.centre_ids && <div className="text-danger">{errors.centre_ids as string}</div>}
                            </div>
                        </div>
                        <div className="col-sm-6">
                            <div className="form-group">
                                <label>Status <span className="required">*</span></label>
                                <select className="form-control" name="status" onChange={handleChange} value={values.status}>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button type="submit" isLoading={isLoading} className="btn btn-primary mr-2">{isEdit ? 'Update' : 'Add'}</Button>
                    <Button className="btn btn-default" onClick={props.handleClose}>Close</Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};

export default AddUpdateCentreUserModal;
